import type { FieldError, ProblemDetails } from '@agent-platform/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly errors: FieldError[];
  readonly requestId: string | undefined;

  constructor(status: number, problem: Partial<ProblemDetails> | null) {
    super(problem?.detail ?? problem?.title ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = problem?.code ?? 'internal';
    this.errors = problem?.errors ?? [];
    this.requestId = problem?.requestId;
  }

  fieldError(path: string): string | undefined {
    return this.errors.find((e) => e.path === path)?.message;
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Headers for a mutation made outside `api()` (e.g. a streaming fetch). */
export function mutationHeaders(): Record<string, string> {
  return csrfToken ? { 'x-csrf-token': csrfToken } : {};
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;

  let response: Response;
  try {
    response = await fetch(`/api/admin${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(0, {
      detail: 'Cannot reach the server. Check your connection and try again.',
    });
  }

  if (response.status === 204) return undefined as T;
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) throw new ApiError(response.status, data as Partial<ProblemDetails> | null);
  return data as T;
}

/** The server was unreachable or briefly unavailable (restart, deploy, proxy): worth retrying. */
export function isTransientError(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.status === 0 || [502, 503, 504].includes(error.status))
  );
}

/** Retry policy for reads: transient failures and other 5xx a few times, never 4xx. */
export function shouldRetry(count: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return count < (isTransientError(error) ? 5 : 2);
}

/** 0.5 s, 1 s, 2 s, 4 s … capped at 8 s. */
export const retryDelay = (count: number): number => Math.min(500 * 2 ** count, 8000);

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.requestId ? `${error.message} (ref ${error.requestId.slice(-8)})` : error.message;
  }
  return 'Something went wrong. Please try again.';
}
